//CRN_20251218 - ⚠️ ngOnInit() called for components/directives & not services ⚠️
//CRN_20260512 - Server-side encryption requires SecurityServerSideEncryption & ServerSideEncryptionWidgets

import { BundleDTO, CompanyDataBundleUsageDTO } from 'src/app/core/models/system/widget/widget-company-data-bundle-usage.dto';
import { CompanyLicenceSummaryDTO } from 'src/app/core/models/system/widget/widget-company-licence-summary.dto';
import { ComplianceAndPercentageDTO } from 'src/app/core/interfaces/general/compliance-and-percentage';
import { ConfigSystemParams, Duration, eSimApiPaths, MobileOperatingSystem } from 'src/app/core/consts';
import { ConfigSystemService } from '../system/config-system/config-system-service';
import { DashboardTotal_Previous_Current_PercentDTO } from 'src/app/core/models/system/dashboard/dashboard-total-previous-current-percent.dto';
import { DateRange } from 'src/app/core/enums'
import { DeviceEndOfLifePerYearDTO } from 'src/app/core/models/system/widget/widget-device-end-of-life-per-year.dto';
import { DeviceVolumeAndHealthTrendDTO } from 'src/app/core/models/system/widget/widget-device-volume-and-health-trend.dto';
import { EncryptionService } from './encryption-service';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from './exceptions-service';
import { forkJoin, interval, Observable, of, shareReplay, startWith, switchMap, timer } from 'rxjs';
import { HttpClientService } from './http-client-service';
import { Injectable } from '@angular/core';
import { IntegerResultAndPercentageChangeDTO } from 'src/app/core/interfaces/general/integer-result-and-percentage-change';
import { IntegerResultAndStringResultDTO } from 'src/app/core/interfaces/general/integer-result-and-string-result';
import { IntegerResultDTO } from 'src/app/core/interfaces/general/integer-result';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';
import { WidgetApplicationDetailsDTO } from 'src/app/core/models/system/widget/widget-application-details.dto';
import { WidgetCompanyAlertsBreakdownDTO } from 'src/app/core/models/system/widget/widget-company-alerts-breakdown.dto';
import { WidgetCompanyCostsBreakdownDTO } from 'src/app/core/models/system/widget/widget-company-costs-breakdown.dto';
import { WidgetCompanyCountryUsageSummaryDTO } from 'src/app/core/models/system/widget/widget-company-country-usage-summary.dto';
import { WidgetCompanyTotalCostDTO } from 'src/app/core/models/system/widget/widget-company-total-cost.dto';
import { WidgetCompanyTotalUsageDTO } from 'src/app/core/models/system/widget/widget-company-total-usage.dto';
import { WidgetCountryDetailsDTO } from 'src/app/core/models/system/widget/widget-country-details.dto';
import { WidgetCountryUsageSummaryDTO } from 'src/app/core/models/system/widget/widget-country-usage-summary.dto';
import { WidgetIccidTodayUsageDTO } from 'src/app/core/models/system/widget/widget-Iccid-today-usage.dto';
import { WidgetIccidTotalCostDTO } from 'src/app/core/models/system/widget/widget-Iccid-total-cost.dto';
import { WidgetIccidTotalUsageDTO } from 'src/app/core/models/system/widget/widget-Iccid-total-usage.dto';
import { WidgetLiveTimeLineDTO } from 'src/app/core/models/system/widget/widget-live-timeline.dto';
import { WidgetSimActivityDTO } from 'src/app/core/models/system/widget/widget-sim-activity.dto';
import { WidgetSimNotInactiveTotalDTO } from 'src/app/core/models/system/widget/widget-sim-not-inactive-total.dto';
import { WidgetSMSAllowanceDTO } from 'src/app/core/models/system/widget/widget-sms-allowance.dto';
import { WidgetZoneUsageSummaryDTO } from 'src/app/core/models/system/widget/widget-zone-usage-summary.dto';

@Injectable({ providedIn: 'root' })

export class WidgetService {

  private encryptedCompanyIdCache: Map<number, { token: Observable<StringResultDTO>; expiresAt: number }>=new Map();
  private encryptedUserIdCache   : Map<number, { token: Observable<StringResultDTO>; expiresAt: number }>=new Map();

  constructor(
    private configSystemService: ConfigSystemService,
    private encryptionService  : EncryptionService,
    private exceptionsService  : ExceptionsService,
    private httpClientService  : HttpClientService
  ) { }

  private deviceClassParam(deviceClass: string | undefined): string {
    try {
      return deviceClass ? "&deviceClass=" + deviceClass : "";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  private getEncryptedCompanyId(companyID: number): Observable<StringResultDTO> {
    try {
      const cached=this.encryptedCompanyIdCache.get(companyID);

      if (cached==undefined || Date.now() >= cached.expiresAt) {
        const encrypted$=this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyID, this.encryptionService.serverSideEncryptionWidgets)
                         .pipe(shareReplay(1));

        this.encryptedCompanyIdCache.set(companyID, { token: encrypted$, expiresAt: Date.now() + Duration._4MinMilli });
        return encrypted$;
      }

      return cached.token;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  private getEncryptedUserId(userID: number): Observable<StringResultDTO> {
    try {
      const cached=this.encryptedUserIdCache.get(userID);

      if (cached==undefined || Date.now() >= cached.expiresAt) {
        const encrypted$=this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userID, this.encryptionService.serverSideEncryptionWidgets)
                         .pipe(shareReplay(1));

        this.encryptedUserIdCache.set(userID, { token: encrypted$, expiresAt: Date.now() + Duration._4MinMilli });
        
        return encrypted$;
      }

      return cached.token;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  private userDomainsParam(userDomains: string | undefined): string {
    try {
      return userDomains ? "&userDomains=" + encodeURIComponent(userDomains) : "";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  public getApplicationTotalUsage(userId: number, iccid: string, companyId: number, year: number, month: number, day: number): Observable<WidgetApplicationDetailsDTO[]> {
    try {
      return forkJoin({
               userIdEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId),
               iccidEncResult    : this.encryptionService.encrypt_AesServerSideForWeb(iccid),
               companyIdEncResult: this.getEncryptedCompanyId(companyId)
             })
             .pipe(
               switchMap(({ userIdEncResult, iccidEncResult, companyIdEncResult }) => {
                 const userIdEnc   =userIdEncResult.result;
                 const iccidEnc    =iccidEncResult.result;
                 const companyIdEnc=companyIdEncResult.result;
                 
                 return this.httpClientService.get<WidgetApplicationDetailsDTO[]>(environment.eSimApi +
                                                                                  eSimApiPaths.Widgets + "/" +
                                                                                  eSimApiPaths.WidgetsGetApplicationSummary +
                                                                                  "?userIdEnc="    + userIdEnc +
                                                                                  "&iccidEnc="     + iccidEnc +
                                                                                  "&companyIdEnc=" + companyIdEnc +
                                                                                  "&year="         + year +
                                                                                  "&month="        + month +
                                                                                  "&day="          + day);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getApplicationTotalUsageUpdates(userId: number, iccid: string, companyId: number,year: number, month: number, day: number): Observable<WidgetApplicationDetailsDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableApplicationSummaryMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getApplicationTotalUsage(userId, iccid, companyId, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getApplicationUsage(userId: number, iccid: string, companyId: number,year: number, month: number, day: number, period: DateRange, groupBy: number): Observable<WidgetApplicationDetailsDTO[]> {
    try {
      return forkJoin({
               userIdEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId),
               iccidEncResult    : this.encryptionService.encrypt_AesServerSideForWeb(iccid),
               companyIdEncResult: this.getEncryptedCompanyId(companyId)
             })
             .pipe(
               switchMap(({ userIdEncResult, iccidEncResult, companyIdEncResult }) => {
                 const userIdEnc   =userIdEncResult.result;
                 const iccidEnc    =iccidEncResult.result;
                 const companyIdEnc=companyIdEncResult.result;
                 
                 return this.httpClientService.get<WidgetApplicationDetailsDTO[]>(environment.eSimApi +
                                                                                  eSimApiPaths.Widgets + "/" +
                                                                                  eSimApiPaths.WidgetsGetApplicationDetails +
                                                                                  "?userIdEnc="    + userIdEnc +
                                                                                  "&iccidEnc="     + iccidEnc +
                                                                                  "&companyIdEnc=" + companyIdEnc +
                                                                                  "&year="         + year +
                                                                                  "&month="        + month +
                                                                                  "&day="          + day +
                                                                                  "&period="       + period +
                                                                                  "&groupBy="      + groupBy);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getApplicationUsageUpdates(userId: number, iccid: string, companyId: number,year: number, month: number, day: number, period: DateRange, groupBy: number): Observable<WidgetApplicationDetailsDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableApplicationSummaryMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getApplicationUsage(userId, iccid, companyId, year, month, day, period, groupBy))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getCompanyDetails(companyID: number): Observable<IntegerResultAndStringResultDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndStringResultDTO>(environment.eSimApi +
                                                                                               eSimApiPaths.Widgets + "/" +
                                                                                               eSimApiPaths.WidgetsGetCompanyDetails +
                                                                                               "?companyIdEnc=" + companyIdEnc);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getCompanyTotalCost(companyID: number, year: number, month: number, day: number): Observable<WidgetCompanyTotalCostDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<WidgetCompanyTotalCostDTO[]>(environment.eSimApi +
                                                                                           eSimApiPaths.Widgets + "/" +
                                                                                           eSimApiPaths.WidgetsGetCompanyTotalCost +
                                                                                           "?companyIdEnc=" + companyIdEnc +
                                                                                           "&year=" + year +
                                                                                           "&month=" + month +
                                                                                           "&day=" + day);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getCompanyTotalCostUpdates(companyID: number, year: number, month: number, day: number): Observable<WidgetCompanyTotalCostDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableCompanyTotalCostMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getCompanyTotalCost(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getCompanyTotalUsage(companyID: number, year: number, month: number, day: number): Observable<WidgetCompanyTotalUsageDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<WidgetCompanyTotalUsageDTO[]>(environment.eSimApi +
                                                                                            eSimApiPaths.Widgets + "/" +
                                                                                            eSimApiPaths.WidgetsGetCompanyTotalUsage +
                                                                                            "?companyIdEnc=" + companyIdEnc +
                                                                                            "&year=" + year +
                                                                                            "&month=" + month +
                                                                                            "&day=" + day);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getCompanyTotalUsageUpdates(companyID: number, year: number, month: number, day: number): Observable<WidgetCompanyTotalUsageDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableCompanyTotalUsageMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getCompanyTotalUsage(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getCompliance_Jailbroken(companyID   : number,
                                  deviceClass?: string): Observable<ComplianceAndPercentageDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;

                            return this.httpClientService.get<ComplianceAndPercentageDTO>(environment.eSimApi +
                                                                                          eSimApiPaths.Widgets + "/" +
                                                                                          eSimApiPaths.WidgetsGetCompliance_Jailbroken +
                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                          this.deviceClassParam(deviceClass));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getCompliance_JailbrokenUpdates(companyID   : number,
                                         deviceClass?: string): Observable<ComplianceAndPercentageDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableCompliance_JailbrokenMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getCompliance_Jailbroken(companyID, deviceClass))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCompliance_PinCode(companyID   : number,
                               deviceClass?: string): Observable<ComplianceAndPercentageDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;

                            return this.httpClientService.get<ComplianceAndPercentageDTO>(environment.eSimApi +
                                                                                          eSimApiPaths.Widgets + "/" +
                                                                                          eSimApiPaths.WidgetsGetCompliance_PinCode +
                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                          this.deviceClassParam(deviceClass));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCompliance_PinCodeUpdates(companyID   : number,
                                      deviceClass?: string): Observable<ComplianceAndPercentageDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableCompliance_PinCodeMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getCompliance_PinCode(companyID, deviceClass))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCompliance_Software(companyID            : number,
                                operatingSystemFilter: MobileOperatingSystem,
                                deviceClass?         : string): Observable<ComplianceAndPercentageDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<ComplianceAndPercentageDTO>(environment.eSimApi +
                                                                                          eSimApiPaths.Widgets + "/" +
                                                                                          eSimApiPaths.WidgetsGetCompliance_Software +
                                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                                          "&operatingSystemFilter=" + operatingSystemFilter +
                                                                                          this.deviceClassParam(deviceClass));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getCompliance_SoftwareUpdates(companyID            : number,
                                       operatingSystemFilter: MobileOperatingSystem,
                                       deviceClass?         : string): Observable<ComplianceAndPercentageDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableCompliance_SoftwareMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getCompliance_Software(companyID, operatingSystemFilter, deviceClass))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getCountryDetails(userId: number, iccid: string, year: number, month: number, day: number): Observable<WidgetCountryDetailsDTO[]> {
    try {
      return forkJoin({
               iccidEncResult : this.encryptionService.encrypt_AesServerSideForWeb(iccid),
               userIdEncResult: this.getEncryptedUserId(userId)
             })
             .pipe(
               switchMap(({ iccidEncResult, userIdEncResult }) => {
                 const iccidEnc =iccidEncResult.result;
                 const userIdEnc=userIdEncResult.result;
                 
                 return this.httpClientService.get<WidgetCountryDetailsDTO[]>(environment.eSimApi +
                                                                              eSimApiPaths.Widgets + "/" +
                                                                              eSimApiPaths.WidgetsGetCountryDetails +
                                                                              "?iccidEnc="  + iccidEnc +
                                                                              "&userIdEnc=" + userIdEnc +
                                                                              "&year="      + year +
                                                                              "&month="     + month +
                                                                              "&day="       + day);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getCountryDetailsUpdates(userId: number, iccid: string, year: number, month: number, day: number): Observable<WidgetCountryDetailsDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableCountryDetailsMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getCountryDetails(userId, iccid, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getCountryUsageSummary(userId: number, iccid: string, year: number, month: number, day: number): Observable<WidgetCountryUsageSummaryDTO[]> {
    try {
      return forkJoin({
               iccidEncResult : this.encryptionService.encrypt_AesServerSideForWeb(iccid),
               userIdEncResult: this.getEncryptedUserId(userId)
             })
             .pipe(
               switchMap(({ iccidEncResult, userIdEncResult }) => {
                 const iccidEnc =iccidEncResult.result;
                 const userIdEnc=userIdEncResult.result;
                 
                 return this.httpClientService.get<WidgetCountryUsageSummaryDTO[]>(environment.eSimApi +
                                                                                   eSimApiPaths.Widgets + "/" +
                                                                                   eSimApiPaths.WidgetsGetCountryUsageSummary +
                                                                                   "?iccidEnc="  + iccidEnc +
                                                                                   "&userIdEnc=" + userIdEnc +
                                                                                   "&year="      + year +
                                                                                   "&month="     + month +
                                                                                   "&day="       + day);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getCountryUsageSummaryUpdates(userId: number, iccid: string, year: number, month: number, day: number): Observable<WidgetCountryUsageSummaryDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableCountryUsageSummaryMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getCountryUsageSummary(userId, iccid, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCompanyDataBundleUsage(userId: number, companyId: number, year: number, month: number, day: number): Observable<CompanyDataBundleUsageDTO> {
    try {
      return forkJoin({
               companyIdEncResult: this.getEncryptedCompanyId(companyId),
               userIdEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId)
             })
             .pipe(
               switchMap(({ companyIdEncResult, userIdEncResult }) => {
                 const companyIdEnc=companyIdEncResult.result;
                 const userIdEnc   =userIdEncResult.result;

                 return this.httpClientService.get<CompanyDataBundleUsageDTO>(environment.eSimApi +
                                                                              eSimApiPaths.Widgets + "/" +
                                                                              eSimApiPaths.WidgetsGetCompanyDataBundleUsage +
                                                                              "?userIdEnc="    + userIdEnc +
                                                                              "&companyIdEnc=" + companyIdEnc +
                                                                              "&year="         + year +
                                                                              "&month="        + month +
                                                                              "&day="          + day);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCompanyLicenceSummary(companyId: number, year: number, month: number, day: number): Observable<CompanyLicenceSummaryDTO> {
    try {
      return this.getEncryptedCompanyId(companyId)
             .pipe(
               switchMap(encResult => {
                 const companyIdEnc=encResult.result;

                 return this.httpClientService.get<CompanyLicenceSummaryDTO>(environment.eSimApi +
                                                                                eSimApiPaths.Widgets + "/" +
                                                                                eSimApiPaths.WidgetsGetCompanyLicenceSummary +
                                                                                "?companyIdEnc=" + companyIdEnc +
                                                                                "&year="         + year +
                                                                                "&month="        + month +
                                                                                "&day="          + day);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCompanyBundleDetails(companyId: number, year: number, month: number, day: number): Observable<BundleDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyId)
             .pipe(
               switchMap(encResult => {
                 const companyIdEnc=encResult.result;

                 return this.httpClientService.get<BundleDTO[]>(environment.eSimApi +
                                                                eSimApiPaths.Widgets + "/" +
                                                                eSimApiPaths.WidgetsGetCompanyBundleDetails +
                                                                "?companyIdEnc=" + companyIdEnc +
                                                                "&year="         + year +
                                                                "&month="        + month +
                                                                "&day="          + day);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCompanyAlertsBreakdown(companyId: number, year: number, month: number, day: number): Observable<WidgetCompanyAlertsBreakdownDTO> {
    try {
      return this.getEncryptedCompanyId(companyId)
             .pipe(
               switchMap(encResult => {
                 const companyIdEnc=encResult.result;

                 return this.httpClientService.get<WidgetCompanyAlertsBreakdownDTO>(environment.eSimApi +
                                                                                    eSimApiPaths.Widgets + "/" +
                                                                                    eSimApiPaths.WidgetsGetCompanyAlertsBreakdown +
                                                                                    "?companyIdEnc=" + companyIdEnc +
                                                                                    "&year="         + year +
                                                                                    "&month="        + month +
                                                                                    "&day="          + day);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCompanyCostsBreakdown(userId: number, companyId: number, year: number, month: number, day: number): Observable<WidgetCompanyCostsBreakdownDTO[]> {
    try {
      return forkJoin({
               companyIdEncResult: this.getEncryptedCompanyId(companyId),
               userIdEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId)
             })
             .pipe(
               switchMap(({ companyIdEncResult, userIdEncResult }) => {
                 const companyIdEnc=companyIdEncResult.result;
                 const userIdEnc   =userIdEncResult.result;

                 return this.httpClientService.get<WidgetCompanyCostsBreakdownDTO[]>(environment.eSimApi +
                                                                                     eSimApiPaths.Widgets + "/" +
                                                                                     eSimApiPaths.WidgetsGetCompanyCostsBreakdown +
                                                                                     "?companyIdEnc=" + companyIdEnc +
                                                                                     "&userIdEnc="    + userIdEnc +
                                                                                     "&year="         + year +
                                                                                     "&month="        + month +
                                                                                     "&day="          + day);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCompanyTimeline(userId: number, companyId: number, rowCount: number): Observable<WidgetLiveTimeLineDTO[]> {
    try {
      return forkJoin({
               companyIdEncResult: this.getEncryptedCompanyId(companyId),
               userIdEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId)
             })
             .pipe(
               switchMap(({ companyIdEncResult, userIdEncResult }) => {
                 const companyIdEnc=companyIdEncResult.result;
                 const userIdEnc   =userIdEncResult.result;

                 return this.httpClientService.get<WidgetLiveTimeLineDTO[]>(environment.eSimApi +
                                                                            eSimApiPaths.Widgets + "/" +
                                                                            eSimApiPaths.WidgetsGetCompanyTimeline +
                                                                            "?companyIdEnc=" + companyIdEnc +
                                                                            "&userIdEnc="    + userIdEnc +
                                                                            "&rowCount="     + rowCount);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCompanySMSAllowance(userId: number, companyId: number, year: number, month: number, day: number): Observable<WidgetSMSAllowanceDTO> {
    try {
      return forkJoin({
               companyIdEncResult: this.getEncryptedCompanyId(companyId),
               userIdEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId)
             })
             .pipe(
               switchMap(({ companyIdEncResult, userIdEncResult }) => {
                 const companyIdEnc=companyIdEncResult.result;
                 const userIdEnc   =userIdEncResult.result;

                 return this.httpClientService.get<WidgetSMSAllowanceDTO>(environment.eSimApi +
                                                                          eSimApiPaths.Widgets + "/" +
                                                                          eSimApiPaths.WidgetsGetCompanySMSAllowance +
                                                                          "?companyIdEnc=" + companyIdEnc +
                                                                          "&userIdEnc="    + userIdEnc +
                                                                          "&year="         + year +
                                                                          "&month="        + month +
                                                                          "&day="          + day);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCompanyCountryUsageSummary(userId: number, companyId: number, year: number, month: number, day: number): Observable<WidgetCompanyCountryUsageSummaryDTO> {
    try {
      return forkJoin({
               companyIdEncResult: this.getEncryptedCompanyId(companyId),
               userIdEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId)
             })
             .pipe(
               switchMap(({ companyIdEncResult, userIdEncResult }) => {
                 const companyIdEnc=companyIdEncResult.result;
                 const userIdEnc   =userIdEncResult.result;

                 return this.httpClientService.get<WidgetCompanyCountryUsageSummaryDTO>(environment.eSimApi +
                                                                                        eSimApiPaths.Widgets + "/" +
                                                                                        eSimApiPaths.WidgetsGetCompanyLatestCountryUsageSummary +
                                                                                        "?companyIdEnc=" + companyIdEnc +
                                                                                        "&userIdEnc="    + userIdEnc +
                                                                                        "&year="         + year +
                                                                                        "&month="        + month +
                                                                                        "&day="          + day);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getZoneUsageBundleSummary(userId: number, companyId: number, bundleId: number, year: number, month: number, day: number): Observable<WidgetZoneUsageSummaryDTO[]> {
    try {
      return forkJoin({
               bundleIdEncResult : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>bundleId),
               companyIdEncResult: this.getEncryptedCompanyId(companyId),
               userIdEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId)
             })
             .pipe(
               switchMap(({ bundleIdEncResult, companyIdEncResult, userIdEncResult }) => {
                 const bundleIdEnc =bundleIdEncResult.result;
                 const companyIdEnc=companyIdEncResult.result;
                 const userIdEnc   =userIdEncResult.result;

                 return this.httpClientService.get<WidgetZoneUsageSummaryDTO[]>(environment.eSimApi +
                                                                                eSimApiPaths.Widgets + "/" +
                                                                                eSimApiPaths.WidgetsGetZoneUsageBundleSummary +
                                                                                "?userIdEnc="    + userIdEnc +
                                                                                "&companyIdEnc=" + companyIdEnc +
                                                                                "&bundleIdEnc="  + bundleIdEnc +
                                                                                "&year="         + year +
                                                                                "&month="        + month +
                                                                                "&day="          + day);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getZoneUsageSummary(userId: number, companyId: number, year: number, month: number, day: number): Observable<WidgetZoneUsageSummaryDTO[]> {
    try {
      return forkJoin({
               companyIdEncResult: this.getEncryptedCompanyId(companyId),
               userIdEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId)
             })
             .pipe(
               switchMap(({ companyIdEncResult, userIdEncResult }) => {
                 const companyIdEnc=companyIdEncResult.result;
                 const userIdEnc   =userIdEncResult.result;

                 return this.httpClientService.get<WidgetZoneUsageSummaryDTO[]>(environment.eSimApi +
                                                                                eSimApiPaths.Widgets + "/" +
                                                                                eSimApiPaths.WidgetsGetZoneUsageSummary +
                                                                                "?companyIdEnc=" + companyIdEnc +
                                                                                "&userIdEnc="    + userIdEnc +
                                                                                "&year="         + year +
                                                                                "&month="        + month +
                                                                                "&day="          + day);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIccidTodayUsage(iccid: string): Observable<WidgetIccidTodayUsageDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(iccid, this.encryptionService.serverSideEncryptionWidgets)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const iccidEncSS=encResult.result;
                            
                            return this.httpClientService.get<WidgetIccidTodayUsageDTO>(environment.eSimApi +
                                                                                        eSimApiPaths.Webbing + "/" +
                                                                                        eSimApiPaths.WebbingGetOnlineDeviceUsage +
                                                                                        "?iccidEncSS=" + iccidEncSS); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIccidTodayUsageUpdates(iccid: string): Observable<WidgetIccidTodayUsageDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableIccidTodayUsageMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIccidTodayUsage(iccid))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIccidTotalCost(iccid: string, year: number, month: number, day: number): Observable<WidgetIccidTotalCostDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(iccid, this.encryptionService.serverSideEncryptionWidgets)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const iccidEnc=encResult.result;
                            
                            return this.httpClientService.get<WidgetIccidTotalCostDTO[]>(environment.eSimApi +
                                                                                         eSimApiPaths.Widgets + "/" +
                                                                                         eSimApiPaths.WidgetsGetIccidTotalCost +
                                                                                         "?iccidEnc=" + iccidEnc +
                                                                                         "&year=" + year +
                                                                                         "&month=" + month +
                                                                                         "&day=" + day);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIccidTotalCostUpdates(iccid: string, year: number, month: number, day: number): Observable<WidgetIccidTotalCostDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableIccidTotalCostMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIccidTotalCost(iccid, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIccidTotalUsage(iccid: string, year: number, month: number, day: number): Observable<WidgetIccidTotalUsageDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(iccid, this.encryptionService.serverSideEncryptionWidgets)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const iccidEnc=encResult.result;
                            
                            return this.httpClientService.get<WidgetIccidTotalUsageDTO[]>(environment.eSimApi +
                                                                                          eSimApiPaths.Widgets + "/" +
                                                                                          eSimApiPaths.WidgetsGetIccidTotalUsage +
                                                                                          "?iccidEnc=" + iccidEnc +
                                                                                          "&year=" + year +
                                                                                          "&month=" + month +
                                                                                          "&day=" + day);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIccidTotalUsageUpdates(iccid: string, year: number, month: number, day: number): Observable<WidgetIccidTotalUsageDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableIccidTotalUsageMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIccidTotalUsage(iccid, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getIntuneDevicesAverageAge(companyID   : number,
                                    previousSync: boolean,
                                    deviceClass?: string,
                                    userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesAverageAge
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneDevicesAverageAgeUpdates(companyID   : number,
                                           previousSync: boolean,
                                           deviceClass?: string,
                                           userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesAverageAgeMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesAverageAge(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIntuneDevicesByCarrier(companyID   : number,
                                   previousSync: boolean,
                                   deviceClass?: string,
                                   userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndStringResultDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesByCarrier
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesByCarrierUpdates(companyID   : number,
                                          previousSync: boolean,
                                          deviceClass?: string,
                                          userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesByCarrierMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesByCarrier(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIntuneDevicesByCompanyManaged(companyID   : number,
                                          previousSync: boolean,
                                          deviceClass?: string,
                                          userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndStringResultDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesByCompanyManaged
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesByCompanyManagedUpdates(companyID   : number,
                                                 previousSync: boolean,
                                                 deviceClass?: string,
                                                 userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesByCompanyManagedMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesByCompanyManaged(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIntuneDevicesByEncrypted(companyID   : number,
                                     previousSync: boolean,
                                     deviceClass?: string,
                                     userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndStringResultDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesByEncrypted
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesByEncryptedUpdates(companyID   : number,
                                            previousSync: boolean,
                                            deviceClass?: string,
                                            userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesByEncryptedMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesByEncrypted(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIntuneDevicesByJailbroken(companyID   : number,
                                      previousSync: boolean,
                                      deviceClass?: string,
                                      userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndStringResultDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesByJailbroken
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesByJailbrokenUpdates(companyID   : number,
                                             previousSync: boolean,
                                             deviceClass?: string,
                                             userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesByJailbrokenMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesByJailbroken(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIntuneDevicesByManagementState(companyID   : number,
                                           previousSync: boolean,
                                           deviceClass?: string,
                                           userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndStringResultDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesByManagementState
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesByManagementStateUpdates(companyID   : number,
                                                  previousSync: boolean,
                                                  deviceClass?: string,
                                                  userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesByManagementStateMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesByManagementState(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIntuneDevicesByManufacturer(companyID   : number,
                                        previousSync: boolean,
                                        deviceClass?: string,
                                        userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndStringResultDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesByManufacturer
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesByManufacturerUpdates(companyID   : number,
                                               previousSync: boolean,
                                               deviceClass?: string,
                                               userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesByManufacturerMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesByManufacturer(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIntuneDevicesByOperatingSystem(companyID   : number,
                                           previousSync: boolean,
                                           deviceClass?: string,
                                           userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndStringResultDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesByOperatingSystem
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesByOperatingSystemUpdates(companyID   : number,
                                                  previousSync: boolean,
                                                  deviceClass?: string,
                                                  userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesByOperatingSystemMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesByOperatingSystem(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIntuneDevicesByOperatingSystemCompliant_Intune(companyID   : number,
                                                           previousSync: boolean,
                                                           deviceClass?: string,
                                                           userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndStringResultDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesByOperatingSystemCompliant_Intune
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesByOperatingSystemCompliant_IntuneUpdates(companyID   : number,
                                                                  previousSync: boolean,
                                                                  deviceClass?: string,
                                                                  userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesByOperatingSystemCompliant_IntuneMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesByOperatingSystemCompliant_Intune(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIntuneDevicesByOperatingSystemCompliant_Ours(companyID   : number,
                                                         previousSync: boolean,
                                                         deviceClass?: string,
                                                         userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndStringResultDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesByOperatingSystemCompliant_Ours
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesByOperatingSystemCompliant_OursUpdates(companyID   : number,
                                                                previousSync: boolean,
                                                                deviceClass?: string,
                                                                userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesByOperatingSystemCompliant_OursMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesByOperatingSystemCompliant_Ours(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getIntuneDevicesByOperatingSystemCompliant_OursMinor(companyID   : number,
                                                              previousSync: boolean,
                                                              deviceClass?: string,
                                                              userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;

                            return this.httpClientService.get<IntegerResultAndStringResultDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesByOperatingSystemCompliant_OursMinor
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesByOperatingSystemCompliant_OursMinorUpdates(companyID   : number,
                                                                     previousSync: boolean,
                                                                     deviceClass?: string,
                                                                     userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesByOperatingSystemCompliant_OursMinorMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesByOperatingSystemCompliant_OursMinor(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesBySupervised(companyID   : number,
                                      previousSync: boolean,
                                      deviceClass?: string,
                                      userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndStringResultDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesBySupervised
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesBySupervisedUpdates(companyID   : number,
                                             previousSync: boolean,
                                             deviceClass?: string,
                                             userDomains?: string): Observable<IntegerResultAndStringResultDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesBySupervisedMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesBySupervised(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getIntuneDevicesCheckedInLast30Days(companyID   : number,
                                             previousSync: boolean,
                                             deviceClass?: string,
                                             userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesCheckedInLast30Days
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneDevicesCheckedInLast30DaysUpdates(companyID   : number,
                                                    previousSync: boolean,
                                                    deviceClass?: string,
                                                    userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesCheckedInLast30DaysMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesCheckedInLast30Days(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getIntuneDevicesEndOfLifePerYearCount(companyID   : number,
                                               deviceClass?: string,
                                               userDomains?: string): Observable<DeviceEndOfLifePerYearDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<DeviceEndOfLifePerYearDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesEndOfLifePerYearCount
                                   + "?companyIdEnc=" + companyIdEnc
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneDevicesEndOfLifePerYearCountUpdates(companyID   : number,
                                                      deviceClass?: string,
                                                      userDomains?: string): Observable<DeviceEndOfLifePerYearDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesEndOfLifePerYearMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesEndOfLifePerYearCount(companyID, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesLess_X_GbRemaining(companyID   : number,
                                            previousSync: boolean,
                                            deviceClass?: string,
                                            userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesLess_X_GbRemaining
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneDevicesLess_X_GbRemainingUpdates(companyID   : number,
                                                   previousSync: boolean,
                                                   deviceClass?: string,
                                                   userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesLess_X_GbRemainingMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesLess_X_GbRemaining(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getIntuneDevicesWithMoreThanOneRegistration(companyID   : number,
                                                     previousSync: boolean,
                                                     deviceClass?: string,
                                                     userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesWithMoreThanOneRegistration
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneDevicesWithMoreThanOneRegistrationUpdates(companyID   : number,
                                                            previousSync: boolean,
                                                            deviceClass?: string,
                                                            userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesWithMoreThanOneUserMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesWithMoreThanOneRegistration(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getIntuneDevicesNotCheckedInLast30Days(companyID   : number,
                                                previousSync: boolean,
                                                deviceClass?: string,
                                                userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesNotCheckedInLast30Days
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneDevicesNotCheckedInLast30DaysUpdates(companyID   : number,
                                                       previousSync: boolean,
                                                       deviceClass?: string,
                                                       userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesNotCheckedInLast30DaysMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesNotCheckedInLast30Days(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getIntuneDevicesNotSupported(companyID   : number,
                                      previousSync: boolean,
                                      deviceClass?: string,
                                      userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesNotSupported
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneDevicesNotSupportedUpdates(companyID   : number,
                                             previousSync: boolean,
                                             deviceClass?: string,
                                             userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesNotSupportedMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesNotSupported(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesNotSupportedEOS(companyID   : number,
                                         previousSync: boolean,
                                         deviceClass?: string,
                                         userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;

                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesNotSupportedEOS
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesNotSupportedEOSUpdates(companyID   : number,
                                                previousSync: boolean,
                                                deviceClass?: string,
                                                userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesNotSupportedEOSMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesNotSupportedEOS(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesOldest(companyID   : number,
                                deviceClass?: string,
                                userDomains?: string): Observable<StringResultDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<StringResultDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesOldest
                                   + "?companyIdEnc=" + companyIdEnc
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneDevicesOldestUpdates(companyID   : number,
                                       deviceClass?: string,
                                       userDomains?: string): Observable<StringResultDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesOldestMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesOldest(companyID, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getIntuneDevicesSupported(companyID   : number,
                                   previousSync: boolean,
                                   deviceClass?: string,
                                   userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesSupported
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneDevicesSupportedUpdates(companyID   : number,
                                          previousSync: boolean,
                                          deviceClass?: string,
                                          userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesSupportedMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesSupported(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesSupportedEOS(companyID   : number,
                                      previousSync: boolean,
                                      deviceClass?: string,
                                      userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;

                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesSupportedEOS
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesSupportedEOSUpdates(companyID   : number,
                                             previousSync: boolean,
                                             deviceClass?: string,
                                             userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesSupportedEOSMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesSupportedEOS(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDevicesTotalCount(companyID   : number,
                                    previousSync: boolean,
                                    deviceClass?: string,
                                    userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesTotalCount
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneDevicesTotalCountUpdates(companyID   : number,
                                           previousSync: boolean,
                                           deviceClass?: string,
                                           userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTotalDeviceCountMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesTotalCount(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getIntuneDevicesTotalNew(companyID   : number,
                                  previousSync: boolean,
                                  deviceClass?: string,
                                  userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesTotalNew
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneDevicesTotalNewUpdates(companyID   : number,
                                         previousSync: boolean,
                                         deviceClass?: string,
                                         userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesTotalNewMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesTotalNew(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getIntuneDevicesXYearsOrOver(companyID   : number,
                                      previousSync: boolean,
                                      deviceClass?: string,
                                      userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDevicesXYearsOrOver
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneDevicesXYearsOrOverUpdates(companyID   : number,
                                             previousSync: boolean,
                                             deviceClass?: string,
                                             userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesXYearsOrOverMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDevicesXYearsOrOver(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDeviceVolumeAndHealthTrend(companyID   : number,
                                             deviceClass?: string,
                                             userDomains?: string): Observable<DeviceVolumeAndHealthTrendDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;

                            return this.httpClientService.get<DeviceVolumeAndHealthTrendDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetIntuneDeviceVolumeAndHealthTrend
                                   + "?companyIdEnc=" + companyIdEnc
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneDeviceVolumeAndHealthTrendUpdates(companyID   : number,
                                                    deviceClass?: string,
                                                    userDomains?: string): Observable<DeviceVolumeAndHealthTrendDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableDevicesVolumeAndHealthTrendMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneDeviceVolumeAndHealthTrend(companyID, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getIntuneUsersWithMoreThanOneDevice(companyID   : number,
                                             previousSync: boolean,
                                             deviceClass?: string,
                                             userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets + "/"
                                   + eSimApiPaths.WidgetsGetIntuneUsersWithMoreThanOneDevice
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneUsersWithMoreThanOneDeviceUpdates(companyID   : number,
                                                    previousSync: boolean,
                                                    deviceClass?: string,
                                                    userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableUsersWithMoreThanOneDeviceMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneUsersWithMoreThanOneDevice(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIntuneUsersWithMoreThanOneDeviceSameType(companyID   : number,
                                                     previousSync: boolean,
                                                     deviceClass?: string,
                                                     userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultAndPercentageChangeDTO>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets + "/"
                                   + eSimApiPaths.WidgetsGetIntuneUsersWithMoreThanOneDeviceSameType
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&previousSync=" + previousSync
                                   + this.deviceClassParam(deviceClass)
                                   + this.userDomainsParam(userDomains));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getIntuneUsersWithMoreThanOneDeviceSameTypeUpdates(companyID   : number,
                                                            previousSync: boolean,
                                                            deviceClass?: string,
                                                            userDomains?: string): Observable<IntegerResultAndPercentageChangeDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableUsersWithMoreThanOneDeviceMilliSecs, Duration._5MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getIntuneUsersWithMoreThanOneDeviceSameType(companyID, previousSync, deviceClass, userDomains))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getLatestAndroid(): Observable<IntegerResultDTO> {
    try {
      return this.httpClientService.get<IntegerResultDTO>(
             environment.eSimApi
             + eSimApiPaths.Widgets
             + "/"
             + eSimApiPaths.WidgetsGetLastestAndroid);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getLatestIos(): Observable<IntegerResultDTO> {
    try {
      return this.httpClientService.get<IntegerResultDTO>(
             environment.eSimApi
             + eSimApiPaths.Widgets
             + "/"
             + eSimApiPaths.WidgetsGetLastestIos);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getLiveTimeline(userId: number, iccid: string): Observable<WidgetLiveTimeLineDTO[]> {
    try {
      return forkJoin({
               iccidEncResult : this.encryptionService.encrypt_AesServerSideForWeb(iccid),
               userIdEncResult: this.getEncryptedUserId(userId)
             })
             .pipe(
               switchMap(({ iccidEncResult, userIdEncResult }) => {
                 const iccidEnc =iccidEncResult.result;
                 const userIdEnc=userIdEncResult.result;
                 
                 return this.httpClientService.get<WidgetLiveTimeLineDTO[]>(
                        environment.eSimApi
                        + eSimApiPaths.Widgets
                        + "/"
                        + eSimApiPaths.WidgetsGetLiveTimeline
                        + "?iccidEnc=" + iccidEnc
                        + "&userIdEnc=" + userIdEnc);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getLiveTimelineUpdates(userId: number, iccid: string): Observable<WidgetLiveTimeLineDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableTimelineMilliSecs, Duration._5SecMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getLiveTimeline(userId, iccid))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getSimActivity(iccid: string,
                        year : number,
                        month: number,
                        day  : number): Observable<WidgetSimActivityDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(iccid, this.encryptionService.serverSideEncryptionWidgets)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const iccidEnc=encResult.result;
                            
                            return this.httpClientService.get<WidgetSimActivityDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetSimActivity
                                   + "?iccidEnc=" + iccidEnc
                                   + "&year=" + year
                                   + "&month=" + month
                                   + "&day=" + day);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getSimActivityUpdates(iccid: string,
                               year : number,
                               month: number,
                               day  : number): Observable<WidgetSimActivityDTO[]> {
    try {
      const now                  =new Date();
      const minutes              =now.getMinutes();
      const minutesToNextHalfHour=30-(minutes % 30);
      const nextHalfHour         =new Date(now.getTime() + minutesToNextHalfHour * 60000);
      nextHalfHour.setSeconds(0);
      nextHalfHour.setMilliseconds(0);
  
      const delayToNextHalfHour=nextHalfHour.getTime()-now.getTime();
      
      return timer(delayToNextHalfHour, Duration._30MinMilli).pipe(
        startWith(0),
        switchMap(() => this.getSimActivity(iccid, year, month, day))
      );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getSimNotInactiveTotal(companyID: number,
                                year     : number,
                                month    : number,
                                day      : number): Observable<WidgetSimNotInactiveTotalDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;
                            
                            return this.httpClientService.get<WidgetSimNotInactiveTotalDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetSimNotInactiveTotal
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&year=" + year
                                   + "&month=" + month
                                   + "&day=" + day);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getSimNotInactiveTotalUpdates(companyID: number,
                                       year     : number,
                                       month    : number,
                                       day      : number): Observable<WidgetSimNotInactiveTotalDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableSimNotInactiveMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getSimNotInactiveTotal(companyID, year, month, day))
            );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getTotalUsageAndLicenceCosts(companyID: number,
                                      year     : number,
                                      month    : number,
                                      day      : number): Observable<DashboardTotal_Previous_Current_PercentDTO[]> {
    try {
      return this.getEncryptedCompanyId(companyID)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEnc=encResult.result;

                            return this.httpClientService.get<DashboardTotal_Previous_Current_PercentDTO[]>(
                                   environment.eSimApi
                                   + eSimApiPaths.Widgets
                                   + "/"
                                   + eSimApiPaths.WidgetsGetTotalUsageAndLicenceCosts
                                   + "?companyIdEnc=" + companyIdEnc
                                   + "&year="  + year
                                   + "&month=" + month
                                   + "&day="   + day);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
}
